import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, List, ListItem, ListItemIcon, ListItemText,
  Box, Stack, CircularProgress, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import { PRICING, PRO_FEATURES, PRO_ENABLED } from '../proConfig';

/**
 * Upgrade modal presenting the two Stripe purchase options:
 *   - $2.99/month Pro subscription (full feature unlock)
 *   - $0.99 one-time HD export
 *
 * `startCheckout(mode)` redirects the browser to Stripe hosted Checkout.
 */
function ProUpgradeModal({ open, onClose, startCheckout }) {
  const [pending, setPending] = useState(null); // 'subscription' | 'oneTime' | null
  const [error, setError] = useState(null);

  const handleBuy = async (mode) => {
    setError(null);
    setPending(mode);
    const result = await startCheckout(mode);
    if (!result.ok) {
      setPending(null);
      setError(
        result.reason === 'not-configured'
          ? 'Pro upgrades are coming soon — checkout is not live yet.'
          : 'Could not start checkout. Please try again.'
      );
    }
    // On success the browser navigates away to Stripe.
  };

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StarIcon sx={{ color: '#FFB300' }} /> Go Pro
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Support nutscaper and unlock the good stuff.
        </Typography>
        <List dense>
          {PRO_FEATURES.map((f) => (
            <ListItem key={f} disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary={f} />
            </ListItem>
          ))}
        </List>

        {error && <Alert severity="info" sx={{ mt: 1 }}>{error}</Alert>}
        {!PRO_ENABLED && !error && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Checkout is being finalized — the buttons below activate once billing
            goes live.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
        <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleBuy('subscription')}
            disabled={Boolean(pending)}
          >
            {pending === 'subscription' ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Box>
                {PRICING.subscription.amount}
                <Typography variant="caption" display="block">
                  {PRICING.subscription.cadence} · full Pro
                </Typography>
              </Box>
            )}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => handleBuy('oneTime')}
            disabled={Boolean(pending)}
          >
            {pending === 'oneTime' ? (
              <CircularProgress size={20} />
            ) : (
              <Box>
                {PRICING.oneTime.amount}
                <Typography variant="caption" display="block">
                  {PRICING.oneTime.cadence} HD export
                </Typography>
              </Box>
            )}
          </Button>
        </Stack>
        <Button onClick={onClose} disabled={Boolean(pending)} size="small">
          Maybe later
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProUpgradeModal;
